import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";
import useLayout from "@/lib/layout/useLayout";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import TableV2 from "@/components/table/TableV2";
import DocumentationReports from "@/components/documentation/DocumentationReports";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { PaginationResponse } from "@/types/pagination";
import {
    formatReportStatus,
    formatReportType,
    isReportOrganizationServiceProvider,
    ReportProcessingStatus,
    ReportTableField,
    ReportTableRow,
} from "@/types/report";
import JSZip from "jszip";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
    ColumnDef,
    RowSelectionState,
    SortingState,
} from "@tanstack/react-table";
import {
    Button,
    Checkbox,
    Chip,
    Divider,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    Select,
    SelectItem,
    addToast,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBars,
    faFileZipper,
    faList,
    faPlus,
    faRotateRight,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, i) =>
    String(CURRENT_YEAR - i),
);

interface ReportTableFilters {
    search?: string;
    serviceProvider?: string;
    grower?: string;
    accountManager?: string;
    enrollmentTier?: string;
}

type AccountManagerLookup = Record<number, { full_name: string }>;

function fieldMatchesLinkedFilters(
    row: ReportTableRow,
    filters: ReportTableFilters,
): boolean {
    const { serviceProvider, grower, accountManager, enrollmentTier } = filters;
    if (!serviceProvider && !grower && !accountManager && !enrollmentTier) {
        return true;
    }

    return row.fields.some((field) => {
        if (grower) {
            if (grower === "null") {
                if (field.grower_id != null) return false;
            } else if (field.grower_id?.toString() !== grower) {
                return false;
            }
        }
        if (serviceProvider) {
            if (serviceProvider === "null") {
                if (field.service_provider_id != null) return false;
            } else if (
                field.service_provider_id?.toString() !== serviceProvider
            ) {
                return false;
            }
        }
        if (accountManager) {
            if (accountManager === "null") {
                if (field.account_manager_id != null) return false;
            } else if (
                field.account_manager_id?.toString() !== accountManager
            ) {
                return false;
            }
        }
        if (
            enrollmentTier &&
            field.active_enrollment_tier?.toString() !== enrollmentTier
        ) {
            return false;
        }
        return true;
    });
}

function matchesSearch(
    row: ReportTableRow,
    search: string,
    accountManagersById: AccountManagerLookup,
): boolean {
    const query = search.toLowerCase();

    if (row.name?.toLowerCase().includes(query)) return true;
    if (row.organization_name?.toLowerCase().includes(query)) return true;
    if (row.service_provider_name?.toLowerCase().includes(query)) return true;

    if (row.account_manager_id != null) {
        const accountManagerName =
            accountManagersById[row.account_manager_id]?.full_name;
        if (accountManagerName?.toLowerCase().includes(query)) return true;
    }

    return row.fields.some((field) => {
        if (field.name.toLowerCase().includes(query)) return true;
        if (field.grower_name.toLowerCase().includes(query)) return true;
        if (field.service_provider_name?.toLowerCase().includes(query)) {
            return true;
        }
        if (field.account_manager_id != null) {
            const accountManagerName =
                accountManagersById[field.account_manager_id]?.full_name;
            if (accountManagerName?.toLowerCase().includes(query)) return true;
        }
        return false;
    });
}

function filterReportTableRows(
    rows: ReportTableRow[],
    filters: ReportTableFilters,
    accountManagersById: AccountManagerLookup,
): ReportTableRow[] {
    return rows.filter((row) => {
        if (
            filters.search &&
            !matchesSearch(row, filters.search, accountManagersById)
        ) {
            return false;
        }
        return fieldMatchesLinkedFilters(row, filters);
    });
}

function sortReportTableRows(
    rows: ReportTableRow[],
    sorting: SortingState,
): ReportTableRow[] {
    const sorted = [...rows];
    const sort = sorting[0] ?? { id: "created_date", desc: true };
    const sortId = sort.id.startsWith("-") ? sort.id.slice(1) : sort.id;

    sorted.sort((a, b) => {
        let cmp = 0;
        switch (sortId) {
            case "name":
                cmp = (a.name ?? "").localeCompare(b.name ?? "");
                break;
            case "grower":
                cmp = (a.organization_name ?? "").localeCompare(
                    b.organization_name ?? "",
                );
                break;
            case "created_date":
                cmp = a.created_date.localeCompare(b.created_date);
                break;
            default:
                cmp = a.created_date.localeCompare(b.created_date);
        }

        if (cmp === 0) {
            return b.id - a.id;
        }

        return sort.desc ? -cmp : cmp;
    });

    return sorted;
}

function sanitizeFileName(name: string): string {
    return name.replace(/[^\w.-]+/g, "_").replace(/_+/g, "_") || "report";
}

function getReportBaseName(report: ReportTableRow): string {
    const label = report.name ?? report.organization_name ?? "report";
    return `${sanitizeFileName(label)}_${report.id}`;
}

async function fetchReportFile(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download file (${response.status})`);
    }
    return response.arrayBuffer();
}

function canDownloadReport(report: ReportTableRow): boolean {
    return report.pdf_url != null || report.excel_url != null;
}

async function downloadReportsZip(
    reports: ReportTableRow[],
    zipFileName: string,
): Promise<number> {
    const zip = new JSZip();
    let fileCount = 0;

    await Promise.all(
        reports.map(async (report) => {
            const baseName = getReportBaseName(report);
            const downloads: Promise<void>[] = [];

            if (report.pdf_url) {
                downloads.push(
                    fetchReportFile(report.pdf_url).then((data) => {
                        zip.file(`${baseName}.pdf`, data);
                        fileCount += 1;
                    }),
                );
            }
            if (report.excel_url) {
                downloads.push(
                    fetchReportFile(report.excel_url).then((data) => {
                        zip.file(`${baseName}.xlsx`, data);
                        fileCount += 1;
                    }),
                );
            }

            await Promise.all(downloads);
        }),
    );

    if (fileCount === 0) {
        throw new Error("No downloadable files found");
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = zipFileName;
    link.click();
    window.URL.revokeObjectURL(url);

    return fileCount;
}

function getReportZipFileName(report: ReportTableRow): string {
    return `${getReportBaseName(report)}.zip`;
}

function getBulkReportsZipFileName(year: string): string {
    return `reports_${year}_${new Date().toISOString().slice(0, 10)}.zip`;
}

function reportStatusColor(
    processingStatus: ReportProcessingStatus,
): "success" | "warning" | "danger" {
    if (processingStatus === ReportProcessingStatus.FAILED) return "danger";
    if (processingStatus === ReportProcessingStatus.COMPLETED) return "success";
    return "warning";
}

function ReportOrganizationCell({
    report,
    userIsSentinel,
    accountManagersById,
}: {
    report: ReportTableRow;
    userIsSentinel: boolean;
    accountManagersById: Record<number, User>;
}) {
    const isServiceProvider = isReportOrganizationServiceProvider(report);
    const accountManagerName =
        report.account_manager_id != null
            ? (accountManagersById[report.account_manager_id]?.full_name ??
                "N/A")
            : "N/A";

    return (
        <div className="flex flex-col">
            <div>{report.organization_name ?? "--"}</div>
            {userIsSentinel && !isServiceProvider && (
                <div className="text-xs text-gray-500">
                    {report.service_provider_name ?? "N/A"}
                    {" - "}
                    {accountManagerName}
                </div>
            )}
            {userIsSentinel && isServiceProvider && (
                <div className="text-xs text-gray-500">
                    {accountManagerName}
                </div>
            )}
        </div>
    );
}

function ReportFieldsCell({
    fields,
    reportName,
    onViewFields,
}: {
    fields: ReportTableField[];
    reportName: string | null;
    onViewFields: (
        reportName: string | null,
        fields: ReportTableField[],
    ) => void;
}) {
    if (fields.length === 0) {
        return <span>--</span>;
    }

    if (fields.length === 1) {
        return <span>{fields[0].name}</span>;
    }

    return (
        <div
            className="py-1"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <Button
                size="sm"
                variant="bordered"
                color="primary"
                className="min-w-[7.5rem]"
                startContent={<FontAwesomeIcon icon={faList} />}
                onPress={() => onViewFields(reportName, fields)}
            >
                View {fields.length} fields
            </Button>
        </div>
    );
}

export default function DocumentationReportsList() {
    const { useGetRequest, axiosRequest, useMutateRequest } = useRequest();
    const queryClient = useQueryClient();
    const { setLayoutState } = useLayout();
    const { showConfirm } = useConfirm();
    const { userIsSentinel, isConsultantOrSentinel } = useAuth();
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [regeneratingReportId, setRegeneratingReportId] = useState<
        number | null
    >(null);
    const [downloadingReportId, setDownloadingReportId] = useState<
        number | null
    >(null);
    const [deletingReportId, setDeletingReportId] = useState<number | null>(
        null,
    );
    const [fieldsModal, setFieldsModal] = useState<{
        reportName: string | null;
        fields: ReportTableRow["fields"];
    } | null>(null);
    const [selectedReports, setSelectedReports] = useState<ReportTableRow[]>(
        [],
    );
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [serviceProvider, setServiceProvider] = useState<
        string | undefined
    >();
    const [grower, setGrower] = useState<string | undefined>();
    const [accountManager, setAccountManager] = useState<string | undefined>();
    const [enrollmentTier, setEnrollmentTier] = useState<string | undefined>();
    const [selectedYear, setSelectedYear] = useState(String(CURRENT_YEAR));
    const [sorting, setSorting] = useState<SortingState>([
        { id: "created_date", desc: true },
    ]);

    useEffect(() => {
        setLayoutState("full_screen");
    }, [setLayoutState]);

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
        enabled: userIsSentinel,
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
        enabled: isConsultantOrSentinel,
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
        enabled: userIsSentinel,
    });

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const accountManagerOptions = useMemo(() => {
        return (
            accountManagersQuery.data?.map((accountManager) => ({
                label: accountManager.full_name,
                key: accountManager.id.toString(),
            })) ?? []
        );
    }, [accountManagersQuery.data]);

    const reportsQuery = useInfiniteQuery<PaginationResponse<ReportTableRow>>({
        queryKey: ["reports", "table", selectedYear],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<ReportTableRow>>({
                url: "reports/table/",
                params: {
                    year: selectedYear,
                    page: pageParam,
                    size: 100,
                },
            });
        },
    });

    const {
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
        isLoading: isReportsLoading,
    } = reportsQuery;

    useEffect(() => {
        if (
            hasNextPage &&
            !isFetchingNextPage &&
            !isReportsLoading &&
            !isFetchNextPageError
        ) {
            fetchNextPage();
        }
    }, [
        hasNextPage,
        isFetchingNextPage,
        isReportsLoading,
        isFetchNextPageError,
        fetchNextPage,
    ]);

    const allReports = useMemo(() => {
        return reportsQuery.data?.pages.flatMap((page) => page.results) ?? [];
    }, [reportsQuery.data?.pages]);

    const tableData = useMemo(() => {
        const filtered = filterReportTableRows(
            allReports,
            {
                search,
                serviceProvider,
                grower,
                accountManager,
                enrollmentTier,
            },
            accountManagersById,
        );
        return sortReportTableRows(filtered, sorting);
    }, [
        allReports,
        search,
        serviceProvider,
        grower,
        accountManager,
        enrollmentTier,
        accountManagersById,
        sorting,
    ]);

    const allPagesLoaded =
        !hasNextPage && !isFetchingNextPage && !isReportsLoading;

    const regenerateReport = useMutateRequest({
        method: "post",
        successMessage: "Report regeneration started",
    });

    const deleteReport = useMutateRequest({
        method: "delete",
        successMessage: "Report deleted",
        errorMessage: "Failed to delete report",
    });

    const invalidateReports = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ["reports", "table"] });
    }, [queryClient]);

    const selectedDownloadableReports = useMemo(
        () => selectedReports.filter(canDownloadReport),
        [selectedReports],
    );

    const bulkActionsDisabledKeys = useMemo(() => {
        const disabled: string[] = [];
        if (selectedReports.length === 0) {
            disabled.push("regenerate");
        }
        if (selectedDownloadableReports.length === 0) {
            disabled.push("download");
        }
        if (selectedReports.length === 0) {
            disabled.push("delete");
        }
        return disabled;
    }, [
        selectedReports.length,
        selectedDownloadableReports.length,
        selectedReports.length,
    ]);

    const handleBulkRegenerate = useCallback(async () => {
        if (selectedReports.length === 0) return;

        setBulkActionLoading(true);
        try {
            await Promise.all(
                selectedReports.map((report) =>
                    axiosRequest({
                        url: `reports/${report.id}/regenerate/`,
                        method: "post",
                    }),
                ),
            );
            invalidateReports();
        } finally {
            setBulkActionLoading(false);
        }
    }, [axiosRequest, selectedReports, invalidateReports]);

    const handleDownloadReports = useCallback(
        async (reports: ReportTableRow[], zipFileName: string) => {
            if (reports.length === 0) return;

            setBulkActionLoading(true);
            try {
                await downloadReportsZip(reports, zipFileName);
            } catch {
                addToast({
                    title: "Failed to download reports",
                    timeout: 3000,
                    color: "danger",
                });
            } finally {
                setBulkActionLoading(false);
            }
        },
        [],
    );

    const handleBulkDownload = useCallback(() => {
        handleDownloadReports(
            selectedDownloadableReports,
            getBulkReportsZipFileName(selectedYear),
        );
    }, [handleDownloadReports, selectedDownloadableReports, selectedYear]);

    const handleRowDownload = useCallback(async (report: ReportTableRow) => {
        setDownloadingReportId(report.id);
        try {
            await downloadReportsZip([report], getReportZipFileName(report));
        } catch {
            addToast({
                title: "Failed to download report",
                timeout: 3000,
                color: "danger",
            });
        } finally {
            setDownloadingReportId(null);
        }
    }, []);

    const handleDeleteReport = useCallback(
        (report: ReportTableRow) => {
            const reportLabel = report.name ?? "this report";
            showConfirm({
                title: "Delete Report",
                description: `Are you sure you want to delete "${reportLabel}"?`,
                color: "danger",
                confirmText: "Delete",
                onConfirm: async () => {
                    setDeletingReportId(report.id);
                    deleteReport.mutate(
                        {
                            url: `reports/${report.id}/`,
                        },
                        {
                            onSuccess: () => {
                                setDeletingReportId(null);
                                setRowSelection((prev) => {
                                    const next = { ...prev };
                                    delete next[String(report.id)];
                                    return next;
                                });
                                invalidateReports();
                            },
                            onError: () => {
                                setDeletingReportId(null);
                            },
                        },
                    );
                },
            });
        },
        [showConfirm, deleteReport, invalidateReports],
    );

    const handleBulkDelete = useCallback(() => {
        if (selectedReports.length === 0) return;

        const count = selectedReports.length;
        showConfirm({
            title: "Delete Reports",
            description: `Are you sure you want to delete ${count} report${count === 1 ? "" : "s"}?`,
            color: "danger",
            confirmText: "Delete",
            onConfirm: async () => {
                setBulkActionLoading(true);
                try {
                    await Promise.all(
                        selectedReports.map((report) =>
                            axiosRequest({
                                url: `reports/${report.id}/`,
                                method: "delete",
                            }),
                        ),
                    );
                    setRowSelection({});
                    invalidateReports();
                    addToast({
                        title:
                            count === 1
                                ? "Report deleted"
                                : `${count} reports deleted`,
                        timeout: 3000,
                        color: "success",
                    });
                } catch {
                    addToast({
                        title: "Failed to delete reports",
                        timeout: 3000,
                        color: "danger",
                    });
                } finally {
                    setBulkActionLoading(false);
                }
            },
        });
    }, [selectedReports, showConfirm, axiosRequest, invalidateReports]);

    useEffect(() => {
        setRowSelection({});
    }, [
        search,
        serviceProvider,
        grower,
        accountManager,
        enrollmentTier,
        selectedYear,
    ]);

    const columns: ColumnDef<ReportTableRow>[] = useMemo(() => {
        return [
            {
                id: "selection",
                header: ({ table }) => (
                    <Checkbox
                        className="pl-4"
                        isSelected={table.getIsAllRowsSelected()}
                        isIndeterminate={table.getIsSomeRowsSelected()}
                        onValueChange={(value) => {
                            table.toggleAllRowsSelected(value);
                        }}
                        isDisabled={allPagesLoaded}
                    />
                ),
                enableSorting: false,
                accessorFn: (row) => row.id,
                cell: ({ row }) => (
                    <Checkbox
                        className="pl-4"
                        isSelected={row.getIsSelected()}
                        onValueChange={(value) => row.toggleSelected(value)}
                    />
                ),
                size: 50,
            },
            {
                id: "grower",
                header: userIsSentinel ? "Farm/CSP" : "Farm",
                accessorFn: (row) => row.organization_name ?? "",
                cell: ({ row }) => (
                    <ReportOrganizationCell
                        report={row.original}
                        userIsSentinel={userIsSentinel}
                        accountManagersById={accountManagersById}
                    />
                ),
                size: 250,
            },
            {
                id: "fields",
                header: "Field",
                accessorFn: (row) =>
                    row.fields.map((field) => field.name).join(", "),
                enableSorting: false,
                cell: ({ row }) => (
                    <ReportFieldsCell
                        fields={row.original.fields}
                        reportName={row.original.name}
                        onViewFields={(reportName, fields) => {
                            setFieldsModal({ reportName, fields });
                        }}
                    />
                ),
                size: 220,
            },
            {
                id: "name",
                header: "Report Name",
                accessorFn: (row) => row.name ?? "",
                cell: ({ row }) => row.original.name ?? "--",
                size: 220,
            },
            {
                id: "report_type",
                header: "Report Type",
                accessorFn: (row) => row.report_type,
                enableSorting: false,
                cell: ({ row }) => formatReportType(row.original.report_type),
                size: 140,
            },
            {
                id: "status",
                header: "Status",
                accessorFn: (row) => row.processing_status,
                enableSorting: false,
                cell: ({ row }) => {
                    const label = formatReportStatus(
                        row.original.processing_status,
                    );
                    return (
                        <Chip
                            size="sm"
                            variant="flat"
                            color={reportStatusColor(
                                row.original.processing_status,
                            )}
                        >
                            {label}
                        </Chip>
                    );
                },
                size: 120,
            },
            {
                id: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => {
                    const report = row.original;
                    const canDownload = canDownloadReport(report);

                    return (
                        <div
                            className="flex flex-row gap-1"
                            onClick={(event) => event.stopPropagation()}
                            onMouseDown={(event) => event.stopPropagation()}
                        >
                            <Button
                                size="sm"
                                variant="flat"
                                isLoading={regeneratingReportId === report.id}
                                startContent={
                                    <FontAwesomeIcon icon={faRotateRight} />
                                }
                                onPress={() => {
                                    setRegeneratingReportId(report.id);
                                    regenerateReport.mutate(
                                        {
                                            url: `reports/${report.id}/regenerate/`,
                                        },
                                        {
                                            onSuccess: () => {
                                                setRegeneratingReportId(null);
                                                invalidateReports();
                                            },
                                            onError: () => {
                                                setRegeneratingReportId(null);
                                            },
                                        },
                                    );
                                }}
                            >
                                Regenerate
                            </Button>
                            <Button
                                size="sm"
                                variant="flat"
                                isDisabled={!canDownload}
                                isLoading={downloadingReportId === report.id}
                                startContent={
                                    <FontAwesomeIcon icon={faFileZipper} />
                                }
                                onPress={() => {
                                    handleRowDownload(report);
                                }}
                            >
                                Download
                            </Button>
                            <Button
                                size="sm"
                                variant="flat"
                                color="danger"
                                isLoading={deletingReportId === report.id}
                                startContent={
                                    <FontAwesomeIcon icon={faTrash} />
                                }
                                onPress={() => {
                                    handleDeleteReport(report);
                                }}
                            >
                                Delete
                            </Button>
                        </div>
                    );
                },
                size: 300,
            },
        ];
    }, [
        userIsSentinel,
        accountManagersById,
        regenerateReport,
        regeneratingReportId,
        downloadingReportId,
        deletingReportId,
        handleRowDownload,
        handleDeleteReport,
        invalidateReports,
        allPagesLoaded,
    ]);

    const isLoading = useMemo(() => {
        return (
            isReportsLoading ||
            (userIsSentinel && serviceProvidersQuery.isLoading) ||
            (userIsSentinel && accountManagersQuery.isLoading) ||
            (isConsultantOrSentinel && growersQuery.isLoading)
        );
    }, [
        isReportsLoading,
        userIsSentinel,
        isConsultantOrSentinel,
        serviceProvidersQuery.isLoading,
        accountManagersQuery.isLoading,
        growersQuery.isLoading,
    ]);

    return (
        <div className="h-full flex flex-col overflow-hidden bg-white">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-col lg:flex-row gap-2 items-center">
                    <FieldFilterBar
                        searchValue={search}
                        onSearchChange={setSearch}
                        serviceProviderId={serviceProvider}
                        onServiceProviderChange={setServiceProvider}
                        serviceProviderOptions={serviceProviderOptions}
                        growerId={grower}
                        onGrowerChange={setGrower}
                        growerOptions={growerOptions}
                        accountManagerId={accountManager}
                        onAccountManagerChange={setAccountManager}
                        accountManagerOptions={accountManagerOptions}
                        enrollmentTier={enrollmentTier}
                        onEnrollmentTierChange={setEnrollmentTier}
                    />
                    <Select
                        selectionMode="single"
                        size="sm"
                        aria-label="Year"
                        className="w-28"
                        selectedKeys={new Set([selectedYear])}
                        onSelectionChange={(keys) => {
                            const value = Array.from(keys)[0];
                            if (value == null) return;
                            setSelectedYear(String(value));
                        }}
                    >
                        {YEAR_OPTIONS.map((y) => (
                            <SelectItem key={y}>{y}</SelectItem>
                        ))}
                    </Select>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Dropdown>
                        <DropdownTrigger>
                            <Button
                                size="sm"
                                color="primary"
                                startContent={<FontAwesomeIcon icon={faBars} />}
                                isDisabled={selectedReports.length === 0}
                                isLoading={bulkActionLoading}
                            >
                                Bulk Actions
                                {selectedReports.length > 0
                                    ? ` (${selectedReports.length})`
                                    : ""}
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                            aria-label="Bulk report actions"
                            disabledKeys={bulkActionsDisabledKeys}
                        >
                            <DropdownItem
                                key="regenerate"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faRotateRight}
                                    />
                                }
                                onPress={handleBulkRegenerate}
                            >
                                Regenerate
                            </DropdownItem>
                            <DropdownItem
                                key="download"
                                startContent={
                                    <FontAwesomeIcon
                                        className="text-primary-500"
                                        icon={faFileZipper}
                                    />
                                }
                                onPress={handleBulkDownload}
                            >
                                Download
                            </DropdownItem>
                            <DropdownItem
                                key="delete"
                                className="text-danger"
                                color="danger"
                                startContent={
                                    <FontAwesomeIcon icon={faTrash} />
                                }
                                onPress={handleBulkDelete}
                            >
                                Delete
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    <Button
                        size="sm"
                        color="secondary"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                        onPress={() => setCreateModalOpen(true)}
                    >
                        New Report
                    </Button>
                </div>
            </div>
            <Divider />
            <TableV2
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                manualSorting
                isLoading={isLoading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                pinnedColumns={["selection", "grower", "name"]}
                showEndMessage
                showScrollbar={true}
                getRowId={(row) => String(row.id)}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onSelectionChange={setSelectedReports}
            />

            <Modal
                isOpen={fieldsModal != null}
                onClose={() => setFieldsModal(null)}
                size="sm"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 pb-0">
                        <span>Report fields</span>
                        {fieldsModal?.reportName && (
                            <span className="text-sm font-normal text-gray-500">
                                {fieldsModal.reportName}
                            </span>
                        )}
                    </ModalHeader>
                    <ModalBody>
                        <ul className="space-y-3 text-sm">
                            {fieldsModal?.fields.map((field) => (
                                <li key={`${field.grower_name}-${field.name}`}>
                                    <div className="font-medium">
                                        {field.name}
                                    </div>
                                    <div className="text-gray-500">
                                        {field.grower_name || "--"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {createModalOpen && (
                <Modal
                    isOpen
                    size="5xl"
                    placement="center"
                    onClose={() => setCreateModalOpen(false)}
                    classNames={{
                        closeButton: "mr-4",
                        wrapper: "overflow-hidden",
                        base: "m-0 sm:m-0 w-[95vw] h-[95vh] max-w-[95vw] max-h-[95vh] rounded-lg",
                    }}
                >
                    <ModalContent className="h-full">
                        <ModalBody className="p-0 overflow-hidden h-full">
                            <DocumentationReports
                                onCreated={() => {
                                    setCreateModalOpen(false);
                                    invalidateReports();
                                }}
                                serviceProvidersQuery={serviceProvidersQuery}
                                growersQuery={growersQuery}
                                accountManagersQuery={accountManagersQuery}
                            />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}
